<template>
  <div ref="tooltipRef" class="flex items-center">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { type Placement } from 'tippy.js';
import { onUnmounted, ref, watch } from 'vue';
import { useTippy } from 'vue-tippy';

const tooltipRef = ref<Element>();
const tippyInstance = ref<ReturnType<typeof useTippy> | null>(null);

const props = defineProps<{
  content?: string | number | undefined | null;
  placement?: Placement;
}>();

watch(
  () => props.content,
  (content) => {
    // Destroy existing tooltip instance if it exists
    if (tippyInstance.value) {
      tippyInstance.value.destroy();
      tippyInstance.value = null;
    }

    // Create new tooltip only if content is provided
    if (content) {
      tippyInstance.value = useTippy(tooltipRef, {
        aria: {
          // `null` when interactive: true, otherwise "describedby"
          content: 'auto',
          // matches `interactive` boolean
          expanded: 'auto',
        },
        content: props.content as string,
        placement: props.placement ?? 'bottom',
        arrow: false,
        followCursor: false,
        maxWidth: 192,
        theme: 'teachino-tippy',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        allowHTML: true,
        interactive: true,
        interactiveBorder: 0,
        duration: 0,
        trigger: 'mouseenter focus',
        appendTo: () => document.body,
      });
    }
  },
  { immediate: true },
);

// Cleanup on component unmount
onUnmounted(() => {
  if (tippyInstance.value) {
    tippyInstance.value.destroy();
    tippyInstance.value = null;
  }
});
</script>

<style scoped>
* >>> .tippy-box[data-theme~='teachino-tippy'] {
  background-color: var(--color-secondary-700);
  border-radius: 12px !important;

  color: var(--color-secondary-200);
}
</style>
